import { Check, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { initiateWithdrawal } from '@/features/subscription/api';
import { useWithdrawableContracts } from '@/hooks/useWithdrawableContracts';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

/**
 * §356a BGB Widerrufs-Flow (L.3) — prefilled mask + confirmation screen.
 * Route: /account/widerruf/:contractType/:contractId. The button that links
 * here only renders when the contract is withdrawable (backend canWithdraw),
 * and the backend re-checks eligibility on submit.
 */
export function WithdrawalRoute() {
  const { contractType, contractId } = useParams<{
    contractType: string;
    contractId: string;
  }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { contracts, loading } = useWithdrawableContracts();
  const S = STRINGS.account.withdrawal;

  const contract = contracts.find(
    (c) => c.contractType === contractType && c.contractId === contractId,
  );

  // Name editable (prefilled), E-Mail editable (prefilled — Abs.2 channel
  // choice). Contract is read-only — the contract being withdrawn must not
  // change. Lazy initializers prefill from the (protected-route) auth user
  // without an effect.
  const [name, setName] = useState(() =>
    user ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim() : '',
  );
  const [email, setEmail] = useState(() => user?.email ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onConfirm() {
    if (!contract) return;
    setSubmitting(true);
    setError(null);
    try {
      await initiateWithdrawal({
        contractType: contract.contractType,
        contractId: contract.contractId,
        customerName: name,
        contractIdentifier: contract.contractIdentifier,
        contactEmail: email,
      });
      setDone(true);
    } catch (e) {
      if (e instanceof ApiError && e.errorCode === 'withdrawal_not_eligible') {
        setError(S.errorNotEligible);
      } else {
        setError(e instanceof ApiError ? e.message : S.errorNotEligible);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Confirmation screen ──
  if (done) {
    return (
      <ContentContainer maxWidth="narrow">
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--color-success)]/20 border border-[color:var(--color-success)]/40">
            <Check className="h-8 w-8 text-[color:var(--color-success)]" strokeWidth={3} />
          </div>
          <h1 className="text-[22px] font-semibold text-dream-navy">{S.confirmHeading}</h1>
          <p className="text-sm leading-relaxed text-dream-navy/80">{S.confirmBody}</p>
          <Button asChild className="w-full">
            <Link to="/account/subscription">{S.backToAccount}</Link>
          </Button>
        </div>
      </ContentContainer>
    );
  }

  // ── Loading / not-withdrawable ──
  if (loading) {
    return (
      <ContentContainer maxWidth="narrow">
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-dream-navy/60" />
        </div>
      </ContentContainer>
    );
  }

  if (!contract) {
    return (
      <ContentContainer maxWidth="narrow">
        <div className="flex flex-col items-start gap-4 py-8">
          <p className="text-sm text-dream-navy/80" data-testid="withdrawal-not-eligible">
            {S.errorNotEligible}
          </p>
          <Button asChild variant="outline">
            <Link to="/account/subscription">{S.backToAccount}</Link>
          </Button>
        </div>
      </ContentContainer>
    );
  }

  // ── Mask ──
  return (
    <ContentContainer maxWidth="narrow">
      <div className="flex flex-col gap-5 py-6">
        <h1 className="text-[22px] font-semibold text-dream-navy">{S.maskHeading}</h1>
        <p className="text-sm leading-relaxed text-dream-navy/80">{S.maskIntro}</p>

        <div className="flex flex-col gap-2">
          <Label htmlFor="withdrawal-name">{S.fieldName}</Label>
          <Input
            id="withdrawal-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="withdrawal-name"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="withdrawal-contract">{S.fieldContract}</Label>
          <Input
            id="withdrawal-contract"
            value={contract.contractIdentifier}
            readOnly
            data-testid="withdrawal-contract"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="withdrawal-email">{S.fieldEmail}</Label>
          <Input
            id="withdrawal-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="withdrawal-email"
          />
        </div>

        {error && (
          <p className="text-sm text-[color:var(--color-error)]" data-testid="withdrawal-error">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-2">
          <Button onClick={onConfirm} disabled={submitting} data-testid="withdrawal-confirm">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : S.confirmButton}
          </Button>
          <Button variant="outline" onClick={() => navigate('/account/subscription')}>
            {S.cancel}
          </Button>
        </div>
      </div>
    </ContentContainer>
  );
}
